/**
 * Admin PromosPage
 *
 * Create and manage discount codes. Discounts are funded from the platform
 * commission (host payouts are never reduced) and validated server-side at
 * booking time.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../layout';
import { useAdminPromos, NewPromoCode } from './hooks/useAdminPromos';

const EMPTY: NewPromoCode = {
  code: '',
  type: 'percent',
  value: 10,
  active: true,
  maxRedemptions: null,
  minNights: null,
  minSubtotal: null,
  expiresAt: '',
};

const formatDate = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

export const PromosPage: React.FC = () => {
  const { promos, isLoading, create, toggleActive, remove } = useAdminPromos();
  const [form, setForm] = useState<NewPromoCode>(EMPTY);
  const [saving, setSaving] = useState(false);

  const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim() || !(form.value > 0)) {
      toast.error('Enter a code and a positive value');
      return;
    }
    setSaving(true);
    try {
      await create(form);
      setForm(EMPTY);
      toast.success('Promo code created');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promo Codes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Discounts come out of the platform commission - host payouts are never reduced.
          </p>
        </div>

        {/* Create */}
        <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="SUMMER10"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm({ ...form, type: e.target.value as 'percent' | 'fixed' })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="percent">Percent off (%)</option>
                <option value="fixed">Fixed off ($)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value ({form.type === 'percent' ? '%' : '$'})
              </label>
              <input
                type="number"
                min="0"
                step={form.type === 'percent' ? '1' : '0.01'}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max uses</label>
              <input
                type="number"
                min="0"
                value={form.maxRedemptions ?? ''}
                onChange={(e) => setForm({ ...form, maxRedemptions: num(e.target.value) })}
                placeholder="∞"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min nights</label>
              <input
                type="number"
                min="0"
                value={form.minNights ?? ''}
                onChange={(e) => setForm({ ...form, minNights: num(e.target.value) })}
                placeholder="-"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min subtotal ($)</label>
              <input
                type="number"
                min="0"
                value={form.minSubtotal ?? ''}
                onChange={(e) => setForm({ ...form, minSubtotal: num(e.target.value) })}
                placeholder="-"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires</label>
              <input
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            Active immediately
          </label>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create code'}
          </button>
        </form>

        {/* List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : promos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No promo codes yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Code</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Discount</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Limits</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Used</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Expires</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promos.map((p) => (
                    <tr key={p.code} className="border-b border-gray-100">
                      <td className="p-3 font-mono text-sm font-semibold text-gray-900">{p.code}</td>
                      <td className="p-3 text-sm">
                        {p.type === 'percent' ? `${p.value}%` : `$${p.value}`}
                      </td>
                      <td className="p-3 text-xs text-gray-500">
                        {p.minNights ? `≥${p.minNights} nights ` : ''}
                        {p.minSubtotal ? `≥$${p.minSubtotal}` : ''}
                        {!p.minNights && !p.minSubtotal ? 'None' : ''}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {p.redemptionCount}
                        {p.maxRedemptions ? ` / ${p.maxRedemptions}` : ''}
                      </td>
                      <td className="p-3 text-sm text-gray-500">{formatDate(p.expiresAt)}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleActive(p.code, !p.active)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                              p.active
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {p.active ? 'Active' : 'Off'}
                          </button>
                          <button
                            onClick={() => remove(p.code)}
                            className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default PromosPage;
